import { createScopedVisitaMediaUploader } from '../services/visitaMediaUploadSafety';
import { getBlockedVisitaForms } from '../services/visitaOfflineContextClient';
import { flushQueuedVisitaDraft } from '../services/visitaDraftSync';
import useConfirmationDialog from '../hooks/useConfirmationDialog';
import { acknowledgeVisitaStructure, getVisitaOfflineEntry } from '../services/visitaOfflineQueue';
import { requestVisitaSync, VISITA_SYNC_REQUESTED, hasPendingVisitaStructure, withVisitaSyncLock } from '../services/visitaLocalFirst';
import { removeVisitaGroupInstance } from '../api/clients/visitasClient';
import { beginVisitaSyncCycle, registerVisitaSyncRunner, reportVisitaSyncFailure, visitaSyncErrorMessage, withVisitaSyncTransfer } from '../services/visitaSyncStatus';
import { createVisitaSyncScheduler } from '../services/visitaSyncScheduler';
import { summarizeVisitaSyncCycle } from '../services/visitaSyncCycle';
import { reconcileAutomaticCopies } from '../services/visitaAutomaticCopies';
import { processDeviceSyncRequests } from '../services/deviceSyncClient';
import { acquireVisitaEngineLease } from '../services/visitaEngineLease';
import { archiveVisitaOperationReceipt } from '../services/visitaOperationReceiptArchive';
import { App as CapacitorApp } from '@capacitor/app';
import AsistenciaScanRecoveryNotice from './rrhh/AsistenciaScanRecoveryNotice';
import DataUsageLifecycle from './DataUsageLifecycle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    ArrowDownTrayIcon,
    ArrowLeftOnRectangleIcon,
    ArrowPathIcon,
    ClipboardDocumentListIcon,
    Bars3Icon,
    BanknotesIcon,
    BeakerIcon,
    BuildingOffice2Icon,
    BellAlertIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronDownIcon,
    ClockIcon,
    Cog6ToothIcon,
    ComputerDesktopIcon,
    CubeIcon,
    DocumentTextIcon,
    HomeIcon,
    LifebuoyIcon,
    MapIcon,
    ServerStackIcon,
    ShoppingCartIcon,
    SparklesIcon,
    TagIcon,
    TruckIcon,
    UserGroupIcon,
    UserIcon,
    WrenchScrewdriverIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../auth/AuthContext';
import { listNotificaciones, markNotificacionAsRead, markAllNotificacionesAsRead } from '../api/clients/notificacionesClient';
import { emitVisitaAssignmentSignal } from '../services/visitaAssignmentSignals';
import { createNotificationRealtimeConnection } from '../services/notificationRealtimeConnection';
import { addVisitaGroup, autosaveVisita, dividirTrabajoVisita, finalizarVisita, generarComprobanteServicio, getVisitaStatus, getVisitaContextRevisions, uploadVisitaImage, uploadVisitaAttachment } from '../api/clients/visitasClient';
import { extractApiErrorMessage } from '../api/httpClient';
import { reconcileVisitaInstanceKeys } from '../services/visitaInstanceReconciler';
import { useToast } from '../ui/ToastProvider';
import { Box, Skeleton } from '@mui/material';
import EmptyState from './EmptyState';
import GlobalPwaControls from './GlobalPwaControls';
import AndroidUpdateControl from './AndroidUpdateControl';
import ReauthDialog from './ReauthDialog';
import AsistenteOperativoSheet from './AsistenteOperativoSheet';
import { isNativePlatform } from '../mobile/capacitorPlatform';
import {
    generateRevisionUuid,
    getOfflineEntriesByUser,
    getOfflinePendingCountByUser,
    removeVisitaOfflineEntry,
    subscribeOfflineQueueChanges,
    upsertVisitaOfflineEntry,
} from '../services/visitaOfflineQueue';
import {
    flushOfflineImageQueue,
    getOfflineImageEntriesByUser,
    getOfflineImagePendingCountByUser,
    removeOfflineImageEntryById,
    subscribeOfflineImageQueueChanges,
} from '../services/visitaOfflineMediaQueue';
import {
    areOperationDependenciesResolved,
    claimVisitaOfflineOperation,
    getOfflineOperationPendingCountByUser,
    getVisitaOfflineOperationsByUser,
    recoverExpiredInFlightOperations,
    removeVisitaOfflineOperation,
    subscribeOfflineOperationQueueChanges,
    updateVisitaOfflineOperationStatus,
} from '../services/visitaOfflineOperationQueue';
import { sendClaimedVisitaFinalization } from '../services/visitaFinalizationSync';
import { sendClaimedVisitaStart } from '../services/visitaStartSync';
import { acknowledgeVisitaSplit } from '../services/visitaSplitSync';
import { isVisitaOwnershipConflict } from '../services/visitaSectionOwnership';
import { startPreparedVisita } from '../api/clients/visitasClient';
import { getNetworkOnlineStatus, subscribeNetworkChanges } from '../mobile/networkService';
import {
    getNativeCompletedOfflineIds,
    mirrorNativeOfflineQueue,
    setNativeForegroundActive,
} from '../mobile/nativeOfflineSyncService';
import ShiftTrackingControl from './ShiftTrackingControl';
import ConnectionIndicator from './ConnectionIndicator';
import AutomaticVisitasPreparation from './AutomaticVisitasPreparation';
import AutomaticVisitaContextUpdates from './AutomaticVisitaContextUpdates';

const GROUP_ICON_BY_KEY = {
    'heroicon-o-home': HomeIcon,
    'heroicon-o-user': UserIcon,
    'heroicon-o-cube': CubeIcon,
    'heroicon-o-building-office': BuildingOffice2Icon,
    'heroicon-o-cog-6-tooth': Cog6ToothIcon,
    'heroicon-o-document-text': DocumentTextIcon,
    'heroicon-o-user-group': UserGroupIcon,
    'heroicon-o-truck': TruckIcon,
    'heroicon-o-wrench-screwdriver': WrenchScrewdriverIcon,
    'heroicon-o-banknotes': BanknotesIcon,
    'heroicon-o-shopping-cart': ShoppingCartIcon,
    'heroicon-o-tag': TagIcon,
    'heroicon-o-computer-desktop': ComputerDesktopIcon,
    'heroicon-o-lifebuoy': LifebuoyIcon,
    'heroicon-o-calendar-days': CalendarDaysIcon,
    'heroicon-o-map': MapIcon,
    'heroicon-o-clipboard-document-list': ClipboardDocumentListIcon,
    'heroicon-o-server-stack': ServerStackIcon,
    'heroicon-o-beaker': BeakerIcon,
    'heroicon-o-arrow-path': ArrowPathIcon,
    'heroicon-o-clock': ClockIcon,
};

const STORAGE_PREFIX = 'react-shell-layout';
const HIDE_UNIMPLEMENTED_MODULES = true;
const EXTERNAL_MENU_ALLOWLIST = new Set([
    'access.credenciales',
    'credenciales.view_propias',
    'access.activos_tecnicos',
]);
const OFFLINE_AUTO_DISCARD_STATES = new Set(['finalizada', 'aprobada', 'cancelada']);

function normalizeMenuLabel(label) {
    if (!label) {
        return 'Modulo';
    }

    return label
        .replace(/^acceso al\s+/i, '')
        .replace(/^acceso\s+/i, '')
        .replace(/^modulo de\s+/i, '')
        .replace(/^modulo\s+/i, '')
        .replace(/\s+/g, ' ')
        .replace(/^./, (char) => char.toUpperCase())
        .trim();
}

function parseStoredObject(value) {
    if (!value) {
        return {};
    }

    try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function getStorageKey(userId, suffix) {
    return `${STORAGE_PREFIX}:${userId || 'anon'}:${suffix}`;
}

function getActivosCacheKey(userId, empresaId) {
    return `${ACTIVOS_CACHE_PREFIX}:${empresaId || 'sin-empresa'}:${userId || 'anon'}`;
}

const AppShellContext = React.createContext(false);

function AppShellContent({ children }) {
    const { user, token, hasPermission, logout, menu, isMenuLoading, isNetworkSessionExpired, setIsNetworkSessionExpired } = useAuth();
    const assignmentUserRef = useRef(user);
    assignmentUserRef.current = user;
    const syncIdentityRef = useRef(null);
    if (!syncIdentityRef.current || syncIdentityRef.current.userId !== user?.id
        || syncIdentityRef.current.empresaId !== user?.empresa_id || syncIdentityRef.current.token !== token) {
        syncIdentityRef.current = { userId: user?.id, empresaId: user?.empresa_id, token };
    }
    const syncIdentity = syncIdentityRef.current;
    const {confirm, confirmationDialog} = useConfirmationDialog(syncIdentity);
    const navigate = useNavigate();
    const toast = useToast();
    const appBrand = import.meta.env.VITE_APP_BRAND || 'Coordinacion';
    const OVERFLOW_DEBUG_STORAGE_KEY = 'react-debug-overflow';
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [isOverflowDebugEnabled, setIsOverflowDebugEnabled] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [apkUpdateInfo, setApkUpdateInfo] = useState(null);
    const [activeNotification, setActiveNotification] = useState(null);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [offlinePendingCount, setOfflinePendingCount] = useState(0);
    const [offlineSyncing, setOfflineSyncing] = useState(false);
    const [offlineMediaPendingCount, setOfflineMediaPendingCount] = useState(0);
    const [offlineMediaSyncing, setOfflineMediaSyncing] = useState(false);
    const [offlineOperationPendingCount, setOfflineOperationPendingCount] = useState(0);
    const [offlineOperationSyncing, setOfflineOperationSyncing] = useState(false);
    const [manualOfflineSyncing, setManualOfflineSyncing] = useState(false);
    const [offlineDetailsOpen, setOfflineDetailsOpen] = useState(false);
    const [offlineDetailsLoading, setOfflineDetailsLoading] = useState(false);
    const [offlineDetailsRows, setOfflineDetailsRows] = useState([]);
    const [isOnline, setIsOnline] = useState(
        typeof navigator === 'undefined' ? true : navigator.onLine,
    );
    const [isReauthDismissed, setIsReauthDismissed] = useState(false);
    const [isReauthModalOpen, setIsReauthModalOpen] = useState(false);
    const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
    const isSessionExpiredRef = useRef(false);
    const shellExecutorId = useMemo(
        () => `appshell_${user?.id || '0'}_${Math.random().toString(36).slice(2, 9)}`,
        [user?.id],
    );
    const profileMenuRef = useRef(null);
    const visitaStatusCacheRef = useRef(new Map());
    const offlineSyncRunningRef = useRef(false);
    const offlineSyncSchedulerRef = useRef(null);
    const offlineSyncPromiseRef = useRef(null);
    const offlineSyncRetryRef = useRef(false);
    const offlineSyncRequestedRef = useRef(false);
    const offlineSyncErrorsRef = useRef(new Map());
    const transientSyncFailureRef = useRef(false);
    const userId = user?.id || 'anon';

    const refreshOfflinePendingCount = useCallback(async () => {
        const count = await getOfflinePendingCountByUser(user?.id);
        setOfflinePendingCount(count);
    }, [user?.id]);

    const refreshOfflineMediaPendingCount = useCallback(async () => {
        const count = await getOfflineImagePendingCountByUser(user?.id);
        setOfflineMediaPendingCount(count);
    }, [user?.id]);

    const refreshOfflineOperationPendingCount = useCallback(async () => {
        const count = await getOfflineOperationPendingCountByUser(user?.id);
        setOfflineOperationPendingCount(count);
    }, [user?.id]);

    const notifiedConflictsRef = useRef(new Set());

    const notifyTerminalVisitaConflict = useCallback((visitaId, estado) => {
        if (!visitaId) {
            return;
        }

        const conflictKey = `${visitaId}:${estado}`;
        if (!notifiedConflictsRef.current.has(conflictKey)) {
            notifiedConflictsRef.current.add(conflictKey);
            toast.warning(
                `Visita N° ${visitaId} se encuentra ${estado} en el servidor. Los pendientes locales fueron retenidos sin descarte para conciliación manual.`,
                { duration: 8000 }
            );
        }
    }, [toast]);

    const getVisitaEstadoActual = useCallback(async (visitaId) => {
        if (!token || !visitaId) {
            return null;
        }

        const cache = visitaStatusCacheRef.current;
        const cacheKey = String(visitaId);
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        try {
            const statusPayload = await getVisitaStatus(token, visitaId);
            const estado = String(statusPayload?.estado || '').toLowerCase();
            cache.set(cacheKey, estado);
            return estado;
        } catch {
            return null;
        }
    }, [token]);

    const flushOfflineQueue = useCallback(async ({ visitIds = null, syncToken = token, watermark = null, lease = null, retryBlocked = false } = {}) => {
        if (!token || !user?.id || !navigator.onLine || isSessionExpiredRef.current) {
            return { unauthorized: Boolean(isSessionExpiredRef.current) };
        }

        visitaStatusCacheRef.current.clear();

        const blockedForms = await getBlockedVisitaForms(user);
        const entries = await getOfflineEntriesByUser(user.id);
        if (entries.length === 0) {
            setOfflineSyncing(false);
            await refreshOfflinePendingCount();
            return { success: true };
        }

        setOfflineSyncing(true);

        for (const entry of entries) {
            if (syncIdentityRef.current !== syncIdentity) break;
            if (visitIds && !visitIds.includes(Number(entry.visitaId))) continue;
            if (watermark && !watermark.some(item => item.kind === 'draft' && item.visita_id === Number(entry.visitaId) && item.revision_uuid === entry.revisionId)) continue;
            if (isSessionExpiredRef.current || isNetworkSessionExpired) {
                break;
            }

            if (!entry?.visitaId || !entry?.payload) {
                continue;
            }

            const errorKey = `draft:${entry.key}`;
            const blockedKey = `blocked:${user.empresa_id}:${user.id}:${entry.visitaId}:${entry.revisionId}`;
            if (retryBlocked) offlineSyncErrorsRef.current.delete(blockedKey);
            if (offlineSyncErrorsRef.current.has(blockedKey)) continue;
            offlineSyncErrorsRef.current.delete(errorKey);

            if (blockedForms.has(Number(entry.visitaId))) {
                offlineSyncErrorsRef.current.set(errorKey, 'Los formularios cambiaron. Los cambios locales se conservan hasta revisar la actualización.');
                continue;
            }
            const estadoActual = await getVisitaEstadoActual(entry.visitaId);
            if (estadoActual && OFFLINE_AUTO_DISCARD_STATES.has(estadoActual)) {
                offlineSyncErrorsRef.current.set(errorKey, 'La visita cambió de estado. Los cambios locales se conservaron para revisión.');
                continue;
            }

            let revisionId = entry.revisionId;
            if (!revisionId) {
                revisionId = generateRevisionUuid();
                await upsertVisitaOfflineEntry({
                    userId: user.id,
                    visitaId: entry.visitaId,
                    payload: entry.payload,
                    revisionId,
                });
            }

            try {
                const result = await flushQueuedVisitaDraft({
                    userId: user.id, visitaId: entry.visitaId,
                    allowCausalRebase: !syncToken.startsWith('ds1.'),
                    isCurrent: () => syncIdentityRef.current === syncIdentity && !isSessionExpiredRef.current,
                    send: (payload, options) => lease ? lease.transfer(() => autosaveVisita(syncToken, entry.visitaId, payload, options)) : autosaveVisita(syncToken, entry.visitaId, payload, options),
                });
                if (result.blocked || result.empty) continue;
                const responseData = result.data || {};
                const discardedInstances = Array.isArray(responseData?.discarded_instances) ? responseData.discarded_instances : [];

                if (result.applied && discardedInstances.length > 0) {
                    // acknowledgeVisitaDraft conserva primero el conflicto de la revisión enviada.
                    toast.warning(
                        `Visita N° ${entry.visitaId}: se detectaron instancias descartadas en el servidor (${discardedInstances.join(', ')}). Datos locales preservados.`,
                        { duration: 8000 }
                    );
                }

                if (!result.applied) {
                    reportVisitaSyncFailure(user.id, entry.visitaId, responseData?.message || responseData?.error || 'El servidor no confirmó los cambios.\n\nSe conservan para reintentar.');
                    offlineSyncErrorsRef.current.set(errorKey, responseData?.message || responseData?.error || 'El servidor no confirmó la revisión enviada. Borrador conservado para reintentar.');
                }
            } catch (error) {
                if (syncIdentityRef.current !== syncIdentity) break;
                if ([403, 409, 422].includes(error?.response?.status)) offlineSyncErrorsRef.current.set(blockedKey, 'La revisión requiere reevaluar su bloqueo.');
                reportVisitaSyncFailure(user.id, entry.visitaId, visitaSyncErrorMessage(error));
                offlineSyncErrorsRef.current.set(errorKey, extractApiErrorMessage(error, 'No se pudo enviar el borrador. Datos locales conservados.'));
                if (error?.response?.status === 401) {
                    isSessionExpiredRef.current = true;
                    setIsNetworkSessionExpired(true);
                    setOfflineSyncing(false);
                    await refreshOfflinePendingCount();
                    return { unauthorized: true };
                }

                if (!error?.response || [408,423,429,500,502,503,504].includes(error.response.status)) transientSyncFailureRef.current = true;
                if (error?.code === 'ERR_NETWORK') {
                    setIsOnline(false);
                    break;
                }

                const responseError = String(error?.response?.data?.error || '').toLowerCase();
                if (error?.response?.status === 422 && (responseError.includes('estado: aprobada') || responseError.includes('estado: cancelada') || responseError.includes('no editable en estado: aprobada') || responseError.includes('no editable en estado: cancelada') || responseError.includes('finalizada') || responseError.includes('no editable'))) {
                    offlineSyncErrorsRef.current.set(errorKey, 'La visita no admite cambios. El borrador local se conserva para revisión.');
                    continue;
                }
            }
        }

        setOfflineSyncing(false);
        await refreshOfflinePendingCount();
        return { success: !isSessionExpiredRef.current, unauthorized: isSessionExpiredRef.current };
    }, [getVisitaEstadoActual, isNetworkSessionExpired, notifyTerminalVisitaConflict, refreshOfflinePendingCount, setIsNetworkSessionExpired, token, user?.id, syncIdentity]);

    const flushOfflineMediaQueue = useCallback(async ({ visitIds = null, syncToken = token, watermark = null, lease = null } = {}) => {
        if (!token || !user?.id || !navigator.onLine || isSessionExpiredRef.current) {
            return { unauthorized: Boolean(isSessionExpiredRef.current) };
        }

        setOfflineMediaSyncing(true);

        try {
            const blockedForms = await getBlockedVisitaForms(user);
            const mediaResult = await flushOfflineImageQueue({
                visitIds, allowedFileIds: watermark?.filter(item => item.kind === 'file').map(item => item.item_key.slice(5)),
                userId: user.id,
                empresaId: user.empresa_id,
                isCurrent: () => syncIdentityRef.current === syncIdentity,
                getRevisions: id => getVisitaContextRevisions(syncToken, [id]),
                uploadItem: createScopedVisitaMediaUploader({ userId: user.id,
                    isCurrent: () => syncIdentityRef.current === syncIdentity && !isSessionExpiredRef.current && !isNetworkSessionExpired,
                    isFormBlocked: id => blockedForms.has(Number(id)),
                    getStructure: () => getVisitaOfflineOperationsByUser(user.id), hasPendingStructure: hasPendingVisitaStructure,
                    hasPendingStart: async visitaId => (await getVisitaOfflineOperationsByUser(user.id))
                        .some(op=>op.type==='iniciar_visita'&&Number(op.visitaId)===Number(visitaId)&&op.status!=='applied'),
                    getStatus: id => getVisitaStatus(syncToken, id), notifyConflict: message => toast.warning(message),
                    upload: ({ visitaId, file, descripcion, metadata, queueEntry, attachmentCommand, attachmentKind }) => withVisitaSyncTransfer(user.id, visitaId,
                        async () => {
                            const task=()=>attachmentKind === 'document' ? uploadVisitaAttachment(syncToken, visitaId, file, attachmentCommand)
                                : uploadVisitaImage(syncToken, visitaId, file, descripcion, metadata, { idempotencyKey: `media:${queueEntry.id}`, attachmentCommand });
                            return lease ? lease.transfer(task) : task();
                        }),
                }),
            });

            if (mediaResult?.unauthorized || isSessionExpiredRef.current) {
                isSessionExpiredRef.current = true;
                setIsNetworkSessionExpired(true);
                setIsReauthDismissed(false);
                return { unauthorized: true };
            }
            for (const itemError of mediaResult?.errors || []) {
                offlineSyncErrorsRef.current.set(`media:${itemError.id}`, itemError.message);
            }
        } finally {
            setOfflineMediaSyncing(false);
            await refreshOfflineMediaPendingCount();
        }
        return { success: !isSessionExpiredRef.current, unauthorized: isSessionExpiredRef.current };
    }, [getVisitaEstadoActual, isNetworkSessionExpired, notifyTerminalVisitaConflict, refreshOfflineMediaPendingCount, setIsNetworkSessionExpired, token, user?.id, syncIdentity]);

    const flushOfflineOperationQueue = useCallback(async ({ retryFailed = false, attemptedOperations = new Set(), visitIds = null, profile = 'normal', lease = null, reviewedSignatureUuid = null } = {}) => {
        if (!token || !user?.id || !navigator.onLine || isSessionExpiredRef.current) {
            return { unauthorized: Boolean(isSessionExpiredRef.current) };
        }

        if (profile !== 'remote') await recoverExpiredInFlightOperations({ userId: user.id });

        const blockedForms = await getBlockedVisitaForms(user);
        const operations = await getVisitaOfflineOperationsByUser(user.id);
        if (operations.length === 0) {
            await refreshOfflineOperationPendingCount();
            return { success: true };
        }

        const [activeDrafts, activeMedia] = await Promise.all([
            getOfflineEntriesByUser(user.id),
            getOfflineImageEntriesByUser(user.id),
        ]);

        setOfflineOperationSyncing(true);
        try {
            for (const operation of operations) {
                if (syncIdentityRef.current !== syncIdentity) return { identityChanged: true, success: false };
                if (profile === 'remote' || visitIds && !visitIds.includes(Number(operation.visitaId))) continue;
                if (operation.type === 'generar_comprobante' && operation.operation_uuid !== reviewedSignatureUuid) {
                    reportVisitaSyncFailure(user.id, operation.visitaId, 'La captura requiere revisión y aceptación expresa con conexión. Se conserva sin firmar ni cerrar automáticamente.');
                    continue;
                }
                await lease?.assert();
                if (isSessionExpiredRef.current || isNetworkSessionExpired) {
                    return { unauthorized: true };
                }

                if (operation.status === 'in_flight' || operation.status === 'conflict' || operation.status === 'applied') {
                    continue;
                }
                if (attemptedOperations.has(operation.id)) {
                    continue;
                }
                if (!retryFailed && operation.status === 'failed' && (operation.retryCount || 0) >= 5) {
                    continue;
                }

                if (blockedForms.has(Number(operation.visitaId))) { reportVisitaSyncFailure(user.id, operation.visitaId, 'Los formularios cambiaron.\n\nLa operación se conserva para revisión.'); continue; }
                let currentActiveDrafts = activeDrafts;
                let currentActiveMedia = activeMedia;
                if (operation.type === 'finalizar_visita') {
                    [currentActiveDrafts, currentActiveMedia] = await Promise.all([
                        getOfflineEntriesByUser(user.id),
                        getOfflineImageEntriesByUser(user.id),
                    ]);
                }

                const dependenciesResolved = areOperationDependenciesResolved(operation, {
                    activeDrafts: currentActiveDrafts,
                    activeMedia: currentActiveMedia,
                    allOperations: operations,
                });

                if (!dependenciesResolved) {
                    continue;
                }

                let claimToken = null;
                try {
                    const estadoActual = await getVisitaEstadoActual(operation.visitaId);
                    if (operation.type !== 'finalizar_visita' && estadoActual && OFFLINE_AUTO_DISCARD_STATES.has(estadoActual)) {
                        await updateVisitaOfflineOperationStatus(operation.id, { status: 'conflict',
                            lastError: 'La visita cambió de estado. Operación local conservada para revisión.' });
                        continue;
                    }

                    // Adquisición exclusiva atómica con fencing y lease por ejecutor único
                    const claimed = await claimVisitaOfflineOperation({
                        operationId: operation.id,
                        ownerId: shellExecutorId,
                        leaseMs: 60000,
                    });
                    if (!claimed) {
                        continue;
                    }

                    claimToken = claimed.claimToken;
                    attemptedOperations.add(operation.id);
                    if (claimed.type === 'iniciar_visita') {
                        const outcome = await sendClaimedVisitaStart({claimed,ownerId:shellExecutorId,empresaId:user.empresa_id,
                            send:(payload,options)=>lease ? lease.transfer(()=>startPreparedVisita(token,claimed.visitaId,payload,options)) : startPreparedVisita(token,claimed.visitaId,payload,options),
                            isCurrent:()=>syncIdentityRef.current.userId===user.id
                                &&syncIdentityRef.current.empresaId===user.empresa_id&&syncIdentityRef.current.token===token});
                        if(outcome.unauthorized){isSessionExpiredRef.current=true;setIsNetworkSessionExpired(true);setIsReauthDismissed(false);return {unauthorized:true};}
                        operation.status=outcome.status;
                        if(outcome.status==='conflict')reportVisitaSyncFailure(user.id,operation.visitaId,outcome.message);
                        continue;
                    }
                    if (claimed.type === 'finalizar_visita') {
                        const outcome = await sendClaimedVisitaFinalization({ claimed, ownerId: shellExecutorId, empresaId: user.empresa_id,
                            send: (payload, options) => lease ? lease.transfer(()=>finalizarVisita(token, claimed.visitaId, payload, options)) : finalizarVisita(token, claimed.visitaId, payload, options),
                            isCurrent: () => syncIdentityRef.current.userId === user.id
                                && syncIdentityRef.current.empresaId === user.empresa_id && syncIdentityRef.current.token === token });
                        if (outcome.unauthorized) {
                            isSessionExpiredRef.current = true;
                            setIsNetworkSessionExpired(true);
                            setIsReauthDismissed(false);
                            return { unauthorized: true };
                        }
                        operation.status = outcome.status;
                        if (outcome.status === 'conflict') reportVisitaSyncFailure(user.id, operation.visitaId, outcome.message);
                        continue;
                    }
                    const rawPayload = typeof claimed.payload === 'string'
                        ? (() => { try { return JSON.parse(claimed.payload); } catch { return {}; } })()
                        : (claimed.payload || {});
                    const payloadClientUuid = rawPayload?.client_uuid || null;
                    const operationUuid = operation.operation_uuid || payloadClientUuid || operation.id;
                    const idempotencyKey = `operation:${operationUuid}`;
                    const opOptions = {
                        idempotencyKey,
                        client_uuid: operationUuid,
                    };

                    let opResponse = null;
                    if (operation.type === 'split_work') {
                        opResponse = await dividirTrabajoVisita(token, operation.visitaId, rawPayload, opOptions);
                    } else if (operation.type === 'add_group') {
                        opResponse = await withVisitaSyncLock(user.id, operation.visitaId,
                            () => addVisitaGroup(token, operation.visitaId, rawPayload, opOptions));
                    } else if (operation.type === 'remove_group') {
                        opResponse = await withVisitaSyncLock(user.id, operation.visitaId,
                            () => removeVisitaGroupInstance(token, operation.visitaId, rawPayload, opOptions));
                    } else if (operation.type === 'generar_comprobante') {
                        opResponse = await generarComprobanteServicio(token, operation.visitaId, rawPayload, opOptions);
                    } else {
                        continue;
                    }

                    await lease?.assert();
                    if (syncIdentityRef.current !== syncIdentity) throw Object.assign(Error('Cambió la cuenta.'),{code:'identity_changed'});
                    const opData = opResponse?.data || opResponse || {};
                    const appliedUuid = opData?.applied_operation_uuid || opData?.operation_uuid || null;
                    const effectConfirmed = operation.type !== 'generar_comprobante' || Number.isSafeInteger(Number(opData?.remito_id))&&Number(opData.remito_id)>0;
                    const isApplied = effectConfirmed && (opData?.success === true || opData?.status === 'applied' || opData?.idempotent === true) && opData?.ignored !== true && opData?.conflict !== true;

                    // Exigencia de ACK coincidente o reconciliación idempotente de existencia previa
                    const matchesAck = Boolean(appliedUuid && operationUuid && appliedUuid === operationUuid);
                    if (isApplied && matchesAck) {
                        await archiveVisitaOperationReceipt({original:claimed,receipt:opData,empresaId:user.empresa_id,isCurrent:()=>syncIdentityRef.current===syncIdentity});
                        if (operation.type === 'split_work') {
                            await acknowledgeVisitaSplit(claimed, opData, user.empresa_id);
                        }
                        if (operation.type === 'add_group' || operation.type === 'remove_group') {
                            const serverInstanceKey = opData?.instance_key || null;
                            const localInstanceKey = rawPayload?.local_instance_key || null;
                            if (operation.type === 'add_group' && serverInstanceKey && localInstanceKey && serverInstanceKey !== localInstanceKey) {
                                const pending = await getVisitaOfflineOperationsByUser(user.id);
                                if (pending.some((op) => op.visitaId === operation.visitaId && op.type === 'remove_group')) {
                                    throw new Error('El servidor cambió la posición de una instancia con eliminaciones pendientes. Datos conservados para revisión.');
                                }
                                await reconcileVisitaInstanceKeys({
                                    visitaId: operation.visitaId,
                                    userId: user?.id,
                                    localInstanceKey,
                                    serverInstanceKey,
                                    serverVisita: opData?.visita || null,
                                    operationUuid,
                                    empresaId: user.empresa_id,
                                });
                            }
                            await acknowledgeVisitaStructure({ userId: user.id, visitaId: operation.visitaId,
                                instanceKey: serverInstanceKey, serverVisita: opData.visita, operationId: operation.id,
                                operationType: operation.type, groupIdx: rawPayload.group_idx });
                        }
                        await removeVisitaOfflineOperation(operation.id, { ownerId: shellExecutorId, claimToken });
                        operation.status = 'applied';
                        if (operation.type === 'split_work') window.dispatchEvent(new CustomEvent('visita-split-acknowledged', {detail:{visitaId:operation.visitaId}}));
                    } else if (isApplied && !appliedUuid) {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'pending',
                            ownerId: shellExecutorId,
                            claimToken,
                            lastError: 'Respuesta sin ACK explícito de operación (applied_operation_uuid). Retenida.',
                        });
                    } else if (opData?.ignored === true) {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'conflict',
                            ownerId: shellExecutorId,
                            claimToken,
                            lastError: 'Operación ignorada por el servidor.',
                        });
                    } else if (opData?.conflict === true || opData?.error === 'version_conflict') {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'conflict', ownerId: shellExecutorId, claimToken,
                            lastError: opData?.message || opData?.error || 'Conflicto detectado en el servidor.',
                        });
                    } else {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'failed',
                            ownerId: shellExecutorId,
                            claimToken,
                            incrementRetry: true,
                            lastError: opData?.error || opData?.message || 'Respuesta no confirmada del servidor.',
                        });
                    }
                } catch (error) {
                    if (syncIdentityRef.current !== syncIdentity || ['sync_lease_lost','identity_changed'].includes(error?.code)) throw Object.assign(error,{code:'identity_changed'});
                    reportVisitaSyncFailure(user.id, operation.visitaId, visitaSyncErrorMessage(error));
                    if (error?.response?.status === 401) {
                        isSessionExpiredRef.current = true;
                        setIsNetworkSessionExpired(true);
                        setIsReauthDismissed(false);
                        if (claimToken) {
                            await updateVisitaOfflineOperationStatus(operation.id, {
                                status: 'pending',
                                ownerId: shellExecutorId,
                                claimToken,
                                lastError: 'Sesión expirada en el servidor. Reautenticación requerida.',
                            });
                        }
                        return { unauthorized: true };
                    }

                    if (!error?.response || [408, 423, 429, 500, 502, 503, 504].includes(error.response.status)) {
                        transientSyncFailureRef.current = true;
                        if (claimToken) {
                            await updateVisitaOfflineOperationStatus(operation.id, {
                                status: 'pending',
                                ownerId: shellExecutorId,
                                claimToken,
                                incrementRetry: true,
                                lastError: 'Fallo de conexión de red.',
                            });
                        }
                        break;
                    }

                    const responseError = String(error?.response?.data?.error || error?.response?.data?.message || '').toLowerCase();
                    if (error?.response?.data?.code === 'group_structure_conflict' || isVisitaOwnershipConflict(error)) {
                        await updateVisitaOfflineOperationStatus(operation.id, { status: 'conflict', ownerId: shellExecutorId,
                            claimToken, lastError: error.response.data.error });
                        continue;
                    }
                    if (error?.response?.status === 422 && (responseError.includes('estado: aprobada') || responseError.includes('estado: cancelada') || responseError.includes('finalizada') || responseError.includes('no editable'))) {
                        await updateVisitaOfflineOperationStatus(operation.id, { status: 'conflict', ownerId: shellExecutorId,
                            claimToken, lastError: 'La visita no admite cambios. Operación conservada para revisión.' });
                        continue;
                    }

                    if (error?.response?.status === 409 && (error?.response?.data?.error === 'version_conflict' || responseError.includes('version_conflict'))) {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'conflict', ownerId: shellExecutorId, claimToken,
                            lastError: 'La versión del servidor cambió. Se conserva la intención original para revisión.',
                        });
                        continue;
                    }

                    if (error?.response?.status === 409
                        && error.response.data?.code === 'idempotency_payload_mismatch') {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'conflict',
                            ownerId: shellExecutorId,
                            claimToken,
                            lastError: 'La clave ya tiene otro contenido en el servidor. Operación conservada para revisión.',
                        });
                        continue;
                    }

                    if (claimToken) {
                        await updateVisitaOfflineOperationStatus(operation.id, {
                            status: 'failed',
                            ownerId: shellExecutorId,
                            claimToken,
                            incrementRetry: true,
                            lastError: responseError || error?.message || 'Error al procesar operación.',
                        });
                    }
                }
            }
        } finally {
            setOfflineOperationSyncing(false);
            await refreshOfflineOperationPendingCount();
        }
        return { success: !isSessionExpiredRef.current, unauthorized: isSessionExpiredRef.current };
    }, [getVisitaEstadoActual, isNetworkSessionExpired, notifyTerminalVisitaConflict, refreshOfflineOperationPendingCount, setIsNetworkSessionExpired, shellExecutorId, token, user?.id, user?.empresa_id, syncIdentity]);

    const flushAllOfflineQueues = useCallback(async ({ retryFailed = false, newCycle = false, visitIds = null, profile = 'normal', executionToken = null, watermark = null, reviewedSignatureUuid = null } = {}) => {
        if (!token || !user?.id || !user?.empresa_id || !navigator.onLine) return {success:false,offline:!navigator.onLine,blocked:true};
        if (profile === 'remote' && (!executionToken?.startsWith('ds1.') || !Array.isArray(watermark))) return {success:false,blocked:true};
        const syncToken = profile === 'remote' ? executionToken : token;
        if (syncIdentityRef.current !== syncIdentity) return { success: false, blocked: true, identityChanged: true };
        if (visitIds && (!Array.isArray(visitIds) || !visitIds.length || visitIds.some(id => !Number.isSafeInteger(id) || id < 1))) throw Error('Alcance de sincronización inválido.');
        if (isSessionExpiredRef.current) {
            return { unauthorized: true };
        }
        if (offlineSyncRunningRef.current) {
            if (profile === 'remote' || visitIds) return { success: false, blocked: true, busy: true };
            offlineSyncRequestedRef.current = true;
            offlineSyncRetryRef.current ||= retryFailed;
            return offlineSyncPromiseRef.current || { busy: true };
        }
        offlineSyncSchedulerRef.current?.cancel();
        offlineSyncRunningRef.current = true;
        transientSyncFailureRef.current = false;
        const finishStatus = beginVisitaSyncCycle(user?.id);
        const work = (async () => {
        const attemptedOperations = new Set();
        let lease;
        try {
            lease = await acquireVisitaEngineLease({userId:user.id,empresaId:user.empresa_id,isCurrent:()=>syncIdentityRef.current===syncIdentity});
            // 1. Operaciones estructurales previas (add_group, split_work)
            const opResult1 = await flushOfflineOperationQueue({ retryFailed, attemptedOperations, visitIds, profile, lease, reviewedSignatureUuid:profile==='normal'?reviewedSignatureUuid:null });
            if (opResult1?.unauthorized || isSessionExpiredRef.current) {
                return { unauthorized: true };
            }

            // 2. Borradores/autosave con respuestas de formulario
            if (syncIdentityRef.current !== syncIdentity) return { success: false, identityChanged: true };
            const draftResult = await flushOfflineQueue({ visitIds, syncToken, watermark, lease, retryBlocked: retryFailed || newCycle });
            if (draftResult?.unauthorized || isSessionExpiredRef.current) {
                return { unauthorized: true };
            }

            // 3. Medios/fotos
            if (syncIdentityRef.current !== syncIdentity) return { success: false, identityChanged: true };
            const mediaResult = await flushOfflineMediaQueue({ visitIds, syncToken, watermark, lease });
            if (mediaResult?.unauthorized || isSessionExpiredRef.current) {
                return { unauthorized: true };
            }

            // 4. Operaciones terminales (finalizar_visita) desbloqueadas tras resolverse borradores y fotos
            if (syncIdentityRef.current !== syncIdentity) return { success: false, identityChanged: true };
            await flushOfflineOperationQueue({ retryFailed, attemptedOperations, visitIds, profile, lease, reviewedSignatureUuid:profile==='normal'?reviewedSignatureUuid:null });
            await lease.assert();
            const copyResult = await reconcileAutomaticCopies({ user, visitIds, executionToken: profile === 'remote' ? executionToken : null, isCurrent: () => syncIdentityRef.current === syncIdentity });
            await lease.assert();
            const [drafts, media, operations] = await Promise.all([getOfflineEntriesByUser(user.id), getOfflineImageEntriesByUser(user.id), getVisitaOfflineOperationsByUser(user.id)]);
            return summarizeVisitaSyncCycle({ drafts, media, operations, visitIds, copiesPending: copyResult.pending,
                isCurrent: syncIdentityRef.current === syncIdentity && !isSessionExpiredRef.current,
                online: navigator.onLine, transientFailure: transientSyncFailureRef.current || copyResult.retryable });
        } catch (error) {
            if (['sync_busy','sync_lease_lost','identity_changed'].includes(error?.code)) return {success:false,blocked:true,reason:error.code};
            throw error;
        } finally {
            try { await lease?.release(); } catch { /* Un relevo conserva el nuevo fencing token. */ }
            offlineSyncRunningRef.current = false;
            offlineSyncPromiseRef.current = null;
            finishStatus();
            if (offlineSyncRequestedRef.current) {
                offlineSyncRequestedRef.current = false;
                const retry = offlineSyncRetryRef.current;
                offlineSyncRetryRef.current = false;
                requestVisitaSync({ retryFailed: retry });
            }
        }
        })();
        offlineSyncPromiseRef.current = work;
        return work;
    }, [flushOfflineMediaQueue, flushOfflineOperationQueue, flushOfflineQueue, isNetworkSessionExpired, user?.id, syncIdentity]);

    useEffect(() => {
        if (!user?.id || !token) return undefined;
        return registerVisitaSyncRunner(user.id, flushAllOfflineQueues);
    }, [user?.id, token, flushAllOfflineQueues]);

    useEffect(() => {
        const scheduler = createVisitaSyncScheduler({
            run: async options => {
                let remoteError,remote;
                try { remote=await processDeviceSyncRequests({run:flushAllOfflineQueues,isCurrent:()=>syncIdentityRef.current===syncIdentity,newCycle:options.newCycle}); }
                catch (error) { remoteError=error; }
                const result=await flushAllOfflineQueues(options);
                if(!remoteError)return {...result,success:result.success&&!remote?.pending,remotePending:remote?.pending||0,blocked:result.blocked||remote?.blocked||false,
                    retryable:!result.identityChanged&&(result.retryable||remote?.retryable||false),retryAfterMs:remote?.retryAfterMs||0};
                const status=remoteError?.response?.status;
                return {...result,success:false,remoteUnconfirmed:true,retryable:!result.identityChanged&&remoteError?.code!=='identity_changed'&&(!status||[408,423,429,500,502,503,504].includes(status))};
            },
            isOnline: () => navigator.onLine,
        });
        offlineSyncSchedulerRef.current = scheduler;
        const requestSync = (event) => {
            scheduler.schedule(event?.detail || {});
        };
        const onResume = () => {
            if (document.visibilityState === 'visible') scheduler.schedule({ immediate: true, newCycle:true });
        };
        window.addEventListener(VISITA_SYNC_REQUESTED, requestSync);
        document.addEventListener('visibilitychange', onResume);
        let disposed = false; let appListener;
        void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            if (isActive) scheduler.schedule({ immediate: true, newCycle: true });
        }).then(listener => { if (disposed) listener.remove(); else appListener = listener; }).catch(() => {});
        return () => {
            disposed = true; appListener?.remove();
            scheduler.dispose();
            if (offlineSyncSchedulerRef.current === scheduler) offlineSyncSchedulerRef.current = null;
            window.removeEventListener(VISITA_SYNC_REQUESTED, requestSync);
            document.removeEventListener('visibilitychange', onResume);
        };
    }, [flushAllOfflineQueues]);

    const mirrorQueuesToNativeWorker = useCallback(async () => {
        if (!token || !user?.id) {
            return;
        }

        const [drafts, media, operations] = await Promise.all([
            getOfflineEntriesByUser(user.id),
            getOfflineImageEntriesByUser(user.id),
            getVisitaOfflineOperationsByUser(user.id),
        ]);

        try {
            await mirrorNativeOfflineQueue({ token, drafts, media, operations });
        } catch {
            // El fallback web sigue siendo funcional si la cola nativa falla.
        }
    }, [token, user?.id]);

    const reconcileNativeWorkerResults = useCallback(async () => {
        if (!user?.id) {
            return;
        }

        try {
            const completedIds = await getNativeCompletedOfflineIds();
            if (completedIds.length === 0) {
                return;
            }

            // Un ID del worker anterior no acredita empresa, propietario, cuerpo
            // ni efecto. Conservarlo y dejar que el motor común recupere recibos
            // exactos; nunca fabricar un ACK ni retirar una cola (tampoco ajena).
            await Promise.all([
                refreshOfflinePendingCount(),
                refreshOfflineMediaPendingCount(),
                refreshOfflineOperationPendingCount(),
            ]);
        } catch {
            // Reintentar en la próxima apertura sin afectar sesión ni UI.
        }
    }, [refreshOfflineMediaPendingCount, refreshOfflineOperationPendingCount, refreshOfflinePendingCount, user?.id]);

    const userInitials = useMemo(() => {
        const raw = String(user?.name || '').trim();
        if (!raw) {
            return 'U';
        }

        const parts = raw.split(/\s+/).filter(Boolean);
        const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
        return initials || 'U';
    }, [user?.name]);

    const totalOfflinePending = offlinePendingCount + offlineMediaPendingCount + offlineOperationPendingCount;
    const isAnyOfflineSyncing = manualOfflineSyncing || offlineSyncing || offlineMediaSyncing || offlineOperationSyncing;

    const handleDiscardAllOffline = useCallback(async () => {
        if (!user?.id || isAnyOfflineSyncing) {
            return;
        }

        const identity = syncIdentityRef.current;
        const [drafts, media, operations] = await Promise.all([
            getOfflineEntriesByUser(user.id),
            getOfflineImageEntriesByUser(user.id),
            getVisitaOfflineOperationsByUser(user.id),
        ]);
        if (identity !== syncIdentityRef.current) return;
        const confirmed = await confirm({title:'Descartar pendientes de este dispositivo', destructive:true, confirmLabel:'Descartar pendientes',
            message:'Se intentarán descartar los pendientes revisados de tu cuenta. No se puede deshacer. Los archivos protegidos y las ediciones posteriores se conservarán.'});
        if (!confirmed || identity !== syncIdentityRef.current || offlineSyncRunningRef.current) return;

        const removalResults = await Promise.all([
            ...drafts.map((entry) => entry.revisionId ? removeVisitaOfflineEntry({ userId: user.id, visitaId: entry.visitaId, expectedRevisionId:entry.revisionId }) : Promise.resolve(false)),
            ...media.map((entry) => removeOfflineImageEntryById(entry.id)),
            ...operations.map((entry) => removeVisitaOfflineOperation(entry.id)),
        ]);

        setOfflineDetailsRows([]);
        setOfflineDetailsOpen(false);
        await Promise.all([
            refreshOfflinePendingCount(),
            refreshOfflineMediaPendingCount(),
            refreshOfflineOperationPendingCount(),
        ]);
        toast.info(removalResults.includes(false)
            ? 'Se descartaron los elementos disponibles. Los archivos protegidos, las operaciones en curso y los cierres ya enviados se conservaron para revisión.'
            : 'Pendientes offline descartados.');
    }, [confirm, isAnyOfflineSyncing, refreshOfflineMediaPendingCount, refreshOfflineOperationPendingCount, refreshOfflinePendingCount, toast, user?.id]);

    const loadNotifications = async () => {
        if (!token) {
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        setNotificationsLoading(true);
        try {
            const response = await listNotificaciones(token, { limit: 20 });
            const rows = Array.isArray(response?.data) ? response.data : [];
            setNotifications(rows);
            setUnreadCount(Number(response?.unread_count || 0));
        } catch (error) {
            toast.error(extractApiErrorMessage(error, 'No se pudieron cargar las notificaciones.'));
        } finally {
            setNotificationsLoading(false);
        }
    };

    const loadOfflineDetails = useCallback(async () => {
        if (!user?.id) {
            setOfflineDetailsRows([]);
            return;
        }

        setOfflineDetailsLoading(true);
        try {
            const [drafts, media, operations] = await Promise.all([
                getOfflineEntriesByUser(user.id),
                getOfflineImageEntriesByUser(user.id),
                getVisitaOfflineOperationsByUser(user.id),
            ]);

            const draftRows = (drafts || []).map((entry) => ({
                id: `draft:${entry.key}`,
                type: 'Borrador',
                visitaId: entry?.visitaId || '-',
                updatedAt: entry?.updatedAt || null,
                lastError: offlineSyncErrorsRef.current.get(`draft:${entry.key}`) || null,
            }));

            const mediaRows = (media || []).map((entry) => ({
                id: `media:${entry.id}`,
                type: 'Archivo',
                visitaId: entry?.visitaId || '-',
                updatedAt: entry?.updatedAt || entry?.createdAt || null,
                lastError: offlineSyncErrorsRef.current.get(`media:${entry.id}`) || null,
            }));

            const operationRows = (operations || []).map((entry) => {
                let statusLabel = 'Pendiente';
                if (entry?.status === 'conflict') {
                    statusLabel = 'En conflicto';
                } else if (entry?.status === 'in_flight') {
                    statusLabel = 'En sincronización';
                } else if (entry?.status === 'failed') {
                    statusLabel = (entry.retryCount || 0) >= 5 ? 'Reintento manual requerido' : 'Error; pendiente de reintento';
                }
                const operationLabels = { add_group: 'Agregar instancias', remove_group: 'Quitar instancias',
                    split_work: 'Dividir trabajo', iniciar_visita: 'Iniciar visita', finalizar_visita: 'Finalizar visita', generar_comprobante: 'Generar documento' };
                const opType = entry?.type ? ` (${operationLabels[entry.type] || 'Pendiente'})` : '';
                const blockedByDependencies = !areOperationDependenciesResolved(entry, {
                    activeDrafts: drafts, activeMedia: media, allOperations: operations,
                });

                return {
                    id: `op:${entry.id}`,
                    type: `Operación${opType}`,
                    status: entry?.status || 'pending',
                    statusLabel,
                    lastError: entry?.lastError || (blockedByDependencies ? 'Espera que se sincronicen los borradores, archivos u operaciones previas de esta visita.' : null),
                    visitaId: entry?.visitaId || '-',
                    updatedAt: entry?.updatedAt || entry?.createdAt || null,
                };
            });

            const merged = [...draftRows, ...mediaRows, ...operationRows].sort((a, b) => {
                const tsA = Date.parse(a?.updatedAt || '') || 0;
                const tsB = Date.parse(b?.updatedAt || '') || 0;
                return tsB - tsA;
            });

            setOfflineDetailsRows(merged);
            return merged;
        } finally {
            setOfflineDetailsLoading(false);
        }
    }, [user?.id]);

    const handleDiscardSingleOffline = useCallback(async (row) => {
        if (!row?.id || !user?.id || isAnyOfflineSyncing) {
            return;
        }

        try {
            if (row.id.startsWith('draft:')) {
                const key = row.id.slice('draft:'.length);
                const parts = key.split(':');
                await removeVisitaOfflineEntry({
                    userId: parts[0] || user.id,
                    visitaId: parts[1] || row.visitaId,
                    force: true,
                });
            } else if (row.id.startsWith('media:')) {
                const removed = await removeOfflineImageEntryById(row.id.slice('media:'.length));
                if (removed === false) {
                    toast.info('El archivo tiene una intención protegida. Se conserva para revisión.');
                    await loadOfflineDetails();
                    return;
                }
            } else if (row.id.startsWith('op:')) {
                const removed = await removeVisitaOfflineOperation(row.id.slice('op:'.length));
                if (!removed) {
                    toast.info('La operación está en curso o el cierre ya fue enviado. Se conserva para revisión.');
                    await loadOfflineDetails();
                    return;
                }
            }

            toast.info(`Pendiente descartado (${row.type}).`);
            await loadOfflineDetails();
            await Promise.all([
                refreshOfflinePendingCount(),
                refreshOfflineMediaPendingCount(),
                refreshOfflineOperationPendingCount(),
            ]);
        } catch {
            toast.error('No se pudo descartar el elemento.');
        }
    }, [isAnyOfflineSyncing, loadOfflineDetails, refreshOfflineMediaPendingCount, refreshOfflineOperationPendingCount, refreshOfflinePendingCount, toast, user?.id]);

    const handleManualOfflineSync = useCallback(async () => {
        if (!isOnline || !token || !user?.id) {
            toast.info('Conectate a internet para sincronizar pendientes.');
            return;
        }
        if (isNetworkSessionExpired || isSessionExpiredRef.current) {
            setIsReauthDismissed(false);
            toast.info('Sesión expirada en el servidor. Reautenticate para sincronizar pendientes.');
            return;
        }
        if (offlineSyncRunningRef.current) {
            toast.info('La sincronización ya está en curso.');
            return;
        }

        setManualOfflineSyncing(true);
        try {
            const before = await loadOfflineDetails();
            const result = await flushAllOfflineQueues({ retryFailed: true });
            if (result?.unauthorized) {
                setIsReauthDismissed(false);
                toast.warning('Sesión expirada. Los pendientes se conservaron; reautenticate para enviarlos.');
                return;
            }
            if (result?.busy) {
                toast.info('La sincronización ya está en curso.');
                return;
            }
            const remaining = await loadOfflineDetails();
            const remainingIds = new Set(remaining.map((row) => row.id));
            const synced = before.filter((row) => !remainingIds.has(row.id)).length;
            if (remaining.length === 0) {
                toast.success('Todos los pendientes fueron sincronizados.');
            } else {
                setOfflineDetailsOpen(true);
                const reason = remaining.find((row) => row.lastError)?.lastError;
                toast.warning(`Se sincronizaron ${synced}. Quedan ${remaining.length} pendientes conservados. ${reason || 'Revisá el detalle de los pendientes.'}`, { duration: 10000 });
            }
        } catch (error) {
            toast.error(extractApiErrorMessage(error, 'No se pudo completar la sincronización. Los pendientes se conservaron.'), { duration: 8000 });
        } finally {
            setManualOfflineSyncing(false);
        }
    }, [flushAllOfflineQueues, isNetworkSessionExpired, isOnline, loadOfflineDetails, toast, token, user?.id]);

    useEffect(() => {
        const mobile = window.matchMedia('(max-width: 900px)').matches;
        setIsMobileViewport(mobile);

        const sidebarState = localStorage.getItem(getStorageKey(userId, 'sidebarCollapsed'));
        setIsSidebarCollapsed(mobile ? false : sidebarState === '1');

        const groupsState = localStorage.getItem(getStorageKey(userId, 'collapsedGroups'));
        setCollapsedGroups(parseStoredObject(groupsState));
    }, [userId]);

    useEffect(() => {
        if (!profileMenuOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!profileMenuRef.current) {
                return;
            }

            if (!profileMenuRef.current.contains(event.target)) {
                setProfileMenuOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setProfileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [profileMenuOpen]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const media = window.matchMedia('(max-width: 900px)');
        const applyState = (matches) => {
            setIsMobileViewport(matches);
            if (matches) {
                setIsSidebarCollapsed(false);
            }
        };

        applyState(media.matches);
        const listener = (event) => applyState(event.matches);

        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', listener);
            return () => media.removeEventListener('change', listener);
        }

        media.addListener(listener);
        return () => media.removeListener(listener);
    }, []);

    useEffect(() => {
        localStorage.setItem(getStorageKey(userId, 'sidebarCollapsed'), isSidebarCollapsed ? '1' : '0');
    }, [userId, isSidebarCollapsed]);

    useEffect(() => {
        localStorage.setItem(getStorageKey(userId, 'collapsedGroups'), JSON.stringify(collapsedGroups));
    }, [userId, collapsedGroups]);

    useEffect(() => {
        setCollapsedGroups((current) => {
            const next = { ...current };
            (menu || []).forEach((group) => {
                if (!(group.name in next)) {
                    next[group.name] = false;
                }
            });
            return next;
        });
    }, [menu]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const params = new URLSearchParams(window.location.search);
        const paramValue = params.get('debugOverflow');
        let enabled = localStorage.getItem(OVERFLOW_DEBUG_STORAGE_KEY) === '1';

        if (paramValue === '1') {
            enabled = true;
            localStorage.setItem(OVERFLOW_DEBUG_STORAGE_KEY, '1');
        } else if (paramValue === '0') {
            enabled = false;
            localStorage.removeItem(OVERFLOW_DEBUG_STORAGE_KEY);
        }

        document.documentElement.classList.toggle('debug-overflow', enabled);
        setIsOverflowDebugEnabled(enabled);

        return () => {
            document.documentElement.classList.remove('debug-overflow');
        };
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [token]);

    useEffect(() => {
        void refreshOfflinePendingCount();
    }, [refreshOfflinePendingCount]);

    useEffect(() => {
        void refreshOfflineMediaPendingCount();
    }, [refreshOfflineMediaPendingCount]);

    useEffect(() => {
        void refreshOfflineOperationPendingCount();
    }, [refreshOfflineOperationPendingCount]);

    useEffect(() => {
        void setNativeForegroundActive(true);

        const heartbeat = setInterval(() => {
            if (document.visibilityState === 'visible') {
                void setNativeForegroundActive(true);
            }
        }, 45000);

        const onVisibilityChange = () => {
            void setNativeForegroundActive(document.visibilityState === 'visible');
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(heartbeat);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            void setNativeForegroundActive(false);
        };
    }, []);

    useEffect(() => {
        void reconcileNativeWorkerResults();
        void mirrorQueuesToNativeWorker();
    }, [mirrorQueuesToNativeWorker, reconcileNativeWorkerResults]);

    useEffect(() => {
        const unsubscribe = subscribeOfflineQueueChanges(() => {
            void refreshOfflinePendingCount();
            void mirrorQueuesToNativeWorker();
        });

        return unsubscribe;
    }, [mirrorQueuesToNativeWorker, refreshOfflinePendingCount]);

    useEffect(() => {
        const unsubscribe = subscribeOfflineImageQueueChanges(() => {
            void refreshOfflineMediaPendingCount();
            void mirrorQueuesToNativeWorker();
        });

        return unsubscribe;
    }, [mirrorQueuesToNativeWorker, refreshOfflineMediaPendingCount]);

    useEffect(() => {
        const unsubscribe = subscribeOfflineOperationQueueChanges(() => {
            void refreshOfflineOperationPendingCount();
            void mirrorQueuesToNativeWorker();
        });

        return unsubscribe;
    }, [mirrorQueuesToNativeWorker, refreshOfflineOperationPendingCount]);

    useEffect(() => {
        let active = true;
        let teardown = () => {};

        const setup = async () => {
            const initialStatus = await getNetworkOnlineStatus();
            if (!active) {
                return;
            }

            setIsOnline(initialStatus);
            if (initialStatus) {
                offlineSyncSchedulerRef.current?.schedule({ immediate: true });
            }

            const unsubscribe = await subscribeNetworkChanges((nextOnline) => {
                setIsOnline(nextOnline);
                if (nextOnline) {
                    offlineSyncSchedulerRef.current?.schedule({ immediate: true, newCycle:true });
                }
            });
            if (!active) unsubscribe();
            else teardown = unsubscribe;
        };

        void setup();

        return () => {
            active = false;
            teardown();
        };
    }, [flushAllOfflineQueues]);

    useEffect(() => {
        if (!token || !user?.id) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            if (navigator.onLine && !offlineSyncSchedulerRef.current?.hasPending()) {
                offlineSyncSchedulerRef.current?.schedule({ immediate: true });
            }
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, [flushAllOfflineQueues, token, user?.id]);

    useEffect(() => {
        if (navigator.onLine) {
            offlineSyncSchedulerRef.current?.schedule({ immediate: true });
        }
    }, [flushAllOfflineQueues]);

    useEffect(() => {
        if (!token || !user?.id) {
            return undefined;
        }

        // El proceso C6 no tiene sidecar ni notificaciones reales.
        if (import.meta.env.VITE_C6_ISOLATED_ORIGIN) return undefined;
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        const inferredWsUrl = `${protocol}//${host}/node/ws/notifications`;
        const baseWsUrl = import.meta.env.VITE_NODE_WS_URL || inferredWsUrl;
        const connection = createNotificationRealtimeConnection({url:baseWsUrl,token,
            isAvailable:()=>navigator.onLine&&document.visibilityState==='visible',onMessage:(event) => {
            try {
                const data = JSON.parse(event.data || '{}');
                const eventName = String(data?.event || '');
                const payload = data?.payload;
                emitVisitaAssignmentSignal(data,assignmentUserRef.current);
                if (eventName === 'device.sync.requested') requestVisitaSync({immediate:true,newCycle:false});

                if (eventName === 'notification.created' && payload && Number(payload.usuario_id) === Number(user.id)) {
                    setNotifications((prev) => {
                        const exists = prev.some((item) => Number(item.id) === Number(payload.id));
                        if (exists) {
                            return prev;
                        }

                        return [payload, ...prev].slice(0, 20);
                    });

                    if (Number(payload.leida) !== 1) {
                        setUnreadCount((prev) => prev + 1);
                    }
                }

                if (eventName === 'notification.read' && payload?.id) {
                    setNotifications((prev) => prev.map((item) => (
                        Number(item.id) === Number(payload.id)
                            ? { ...item, leida: 1, fecha_lectura: payload.fecha_lectura || item.fecha_lectura }
                            : item
                    )));
                }
            } catch (_) {
                // noop
            }
        }});
        const reconnect=()=>connection.connect();
        connection.connect();
        window.addEventListener('online',reconnect);
        document.addEventListener('visibilitychange',reconnect);
        return () => {connection.dispose();window.removeEventListener('online',reconnect);document.removeEventListener('visibilitychange',reconnect);};
    }, [token, user?.id, user?.empresa_id]);

    const handleOpenNotification = async (notification) => {
        setActiveNotification(notification);

        if (!notification || Number(notification.leida) === 1 || !token) {
            return;
        }

        try {
            await markNotificacionAsRead(token, notification.id);
            setNotifications((prev) => prev.map((item) => (
                Number(item.id) === Number(notification.id)
                    ? { ...item, leida: 1, fecha_lectura: new Date().toISOString() }
                    : item
            )));
            setUnreadCount((prev) => Math.max(0, prev - 1));
            setActiveNotification((prev) => (prev ? { ...prev, leida: 1, fecha_lectura: new Date().toISOString() } : prev));
        } catch (error) {
            toast.error(extractApiErrorMessage(error, 'No se pudo marcar la notificación como leída.'));
        }
    };

    const handleMarkSingleRead = async (event, notification) => {
        event.stopPropagation();
        if (!notification || Number(notification.leida) === 1 || !token) {
            return;
        }

        try {
            await markNotificacionAsRead(token, notification.id);
            setNotifications((prev) => prev.map((item) => (
                Number(item.id) === Number(notification.id)
                    ? { ...item, leida: 1, fecha_lectura: new Date().toISOString() }
                    : item
            )));
            setUnreadCount((prev) => Math.max(0, prev - 1));
            toast.success('Notificación marcada como leída');
        } catch (error) {
            toast.error(extractApiErrorMessage(error, 'No se pudo marcar como leída.'));
        }
    };

    const handleMarkAllRead = async () => {
        if (!token || unreadCount <= 0) {
            return;
        }

        try {
            await markAllNotificacionesAsRead(token);
            setNotifications((prev) => prev.map((item) => ({ ...item, leida: 1, fecha_lectura: new Date().toISOString() })));
            setUnreadCount(0);
            toast.success('Todas las notificaciones marcadas como leídas');
        } catch (error) {
            toast.error(extractApiErrorMessage(error, 'No se pudieron marcar todas como leídas.'));
        }
    };

    const handleGoToNotification = () => {
        if (!activeNotification?.url_destino) {
            return;
        }

        window.location.assign(activeNotification.url_destino);
    };

    const navGroups = useMemo(() => {
        if (!Array.isArray(menu)) {
            return [];
        }

        return menu.map((group) => ({
            name: group.name || 'Modulos',
            icon: GROUP_ICON_BY_KEY[group.icon] || ClipboardDocumentListIcon,
            collapsible: group.collapsible !== false,
            sort: group.sort ?? 999,
            items: (group.items || [])
                .filter((item) => item?.permission && hasPermission(item.permission))
                .map((item) => {
                    const candidatePath = item.react_path || item.path || null;
                    const internalPath = typeof candidatePath === 'string' && candidatePath.startsWith('/')
                        ? candidatePath
                        : null;
                    const externalCandidate = item.panel_path || null;
                    const externalPath = typeof externalCandidate === 'string' && externalCandidate.startsWith('/')
                        ? externalCandidate
                        : null;
                    const isExternal = Boolean(item.is_external) && !internalPath && Boolean(externalPath);
                    const isEnabledExternal = isExternal && EXTERNAL_MENU_ALLOWLIST.has(item.permission);

                    return {
                        key: item.permission,
                        label: normalizeMenuLabel(item.label),
                        permission: item.permission,
                        internalPath,
                        externalPath,
                        isExternal,
                        isEnabledExternal,
                    };
                })
                .reduce((accumulator, item) => {
                    const labelKey = String(item.label || '').trim().toLowerCase();
                    const existingIndex = accumulator.findIndex((entry) => String(entry.label || '').trim().toLowerCase() === labelKey);

                    if (existingIndex === -1) {
                        accumulator.push(item);
                        return accumulator;
                    }

                    const existing = accumulator[existingIndex];
                    const existingScore = existing.internalPath ? 4 : (existing.isEnabledExternal ? 3 : (existing.externalPath ? 2 : 1));
                    const candidateScore = item.internalPath ? 4 : (item.isEnabledExternal ? 3 : (item.externalPath ? 2 : 1));

                    if (candidateScore > existingScore) {
                        accumulator[existingIndex] = item;
                    }

                    return accumulator;
                }, [])
                .filter((item) => (HIDE_UNIMPLEMENTED_MODULES ? Boolean(item.internalPath) : true)),
        }))
            .filter((group) => group.items.length > 0)
            .sort((a, b) => a.sort - b.sort);
    }, [menu, hasPermission]);

    const handleToggleGroup = (groupName) => {
        setCollapsedGroups((current) => ({
            ...current,
            [groupName]: !current[groupName],
        }));
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const handleGoToMiCuenta = useCallback(() => {
        setProfileMenuOpen(false);
        navigate('/mi-cuenta');
    }, [navigate]);

    const handleLogoutFromMenu = useCallback(() => {
        setProfileMenuOpen(false);
        logout();
    }, [logout]);

    const empresaNombre = user?.empresa?.nombre || user?.empresa_nombre || appBrand;
    const empresaColor = user?.empresa?.color_principal || '#f59e0b';
    const empresaInitials = useMemo(() => {
        const words = String(empresaNombre || '').trim().split(/\s+/).filter(Boolean);
        if (words.length >= 2) {
            return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
        }
        return String(empresaNombre || 'EM').slice(0, 2).toUpperCase();
    }, [empresaNombre]);

    const empresaLogoCandidates = useMemo(() => {
        const list = [];
        if (user?.empresa?.logo_avatar_url) {
            list.push(user.empresa.logo_avatar_url);
        }
        const empId = user?.empresa?.id || user?.empresa_id;
        if (empId && user?.empresa?.logo_sistema_url) {
            try {
                const origin = new URL(
                    user.empresa.logo_sistema_url,
                    typeof window !== 'undefined' ? window.location.origin : undefined,
                ).origin;
                list.push(`${origin}/storage/logos/sistema/avatar_${empId}.png`);
            } catch {
                list.push(`/storage/logos/sistema/avatar_${empId}.png`);
            }
        }
        if (user?.empresa?.logo_sistema_url) {
            list.push(user.empresa.logo_sistema_url);
        }
        return [...new Set(list.filter(Boolean))];
    }, [user?.empresa?.id, user?.empresa?.logo_avatar_url, user?.empresa?.logo_sistema_url, user?.empresa_id]);

    const [empresaLogoCandidateIndex, setEmpresaLogoCandidateIndex] = useState(0);
    useEffect(() => {
        setEmpresaLogoCandidateIndex(0);
    }, [empresaLogoCandidates]);
    const currentEmpresaLogoUrl = empresaLogoCandidates[empresaLogoCandidateIndex] || null;

    const [collapsedFlyout, setCollapsedFlyout] = useState(null);
    const collapsedFlyoutRef = useRef(null);

    useEffect(() => {
        if (!isSidebarCollapsed || isMobileViewport) {
            setCollapsedFlyout(null);
        }
    }, [isSidebarCollapsed, isMobileViewport]);

    useEffect(() => {
        if (!collapsedFlyout) {
            return undefined;
        }
        const handleOutside = (event) => {
            if (collapsedFlyoutRef.current && !collapsedFlyoutRef.current.contains(event.target)) {
                setCollapsedFlyout(null);
            }
        };
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                setCollapsedFlyout(null);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [collapsedFlyout]);

    const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';

    return (
        <div className="react-shell">
            {confirmationDialog}
            <AutomaticVisitasPreparation />
            <AutomaticVisitaContextUpdates />
            <aside className={`react-shell-sidebar${isSidebarCollapsed ? ' is-collapsed' : ''}${isMobileMenuOpen ? ' is-mobile-open' : ''}${isMobileViewport ? ' is-mobile-viewport' : ''}`}>
                <div className="react-shell-sidebar-head">
                    {isSidebarCollapsed && !isMobileViewport ? (
                        <button
                            type="button"
                            className="react-shell-collapsed-avatar"
                            title={`${empresaNombre} — Expandir barra lateral`}
                            aria-label="Expandir barra lateral"
                            onClick={() => setIsSidebarCollapsed(false)}
                        >
                            <div className="react-shell-collapsed-avatar-inner">
                                {currentEmpresaLogoUrl ? (
                                    <img
                                        src={currentEmpresaLogoUrl}
                                        alt={empresaNombre}
                                        className="react-shell-collapsed-avatar-img"
                                        onError={() => setEmpresaLogoCandidateIndex((prev) => prev + 1)}
                                    />
                                ) : (
                                    <div
                                        className="react-shell-collapsed-avatar-initials"
                                        style={{ backgroundColor: empresaColor }}
                                    >
                                        {empresaInitials}
                                    </div>
                                )}
                                <div className="react-shell-collapsed-avatar-overlay" aria-hidden="true">
                                    <ChevronLeftIcon className="react-shell-collapsed-avatar-chevron" />
                                </div>
                            </div>
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="react-shell-brand"
                                title={isMobileViewport ? empresaNombre : `${empresaNombre} — Colapsar barra lateral`}
                                onClick={() => {
                                    if (!isMobileViewport) {
                                        setIsSidebarCollapsed(true);
                                    }
                                }}
                                disabled={isMobileViewport}
                            >
                                {empresaNombre}
                            </button>
                            <button
                                type="button"
                                className="react-shell-collapse"
                                title="Colapsar barra lateral"
                                aria-label="Colapsar barra lateral"
                                onClick={() => {
                                    if (!isMobileViewport) {
                                        setIsSidebarCollapsed((value) => !value);
                                    }
                                }}
                                disabled={isMobileViewport}
                            >
                                <ChevronLeftIcon className={`react-shell-collapse-icon${isSidebarCollapsed ? ' is-collapsed' : ''}`} aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>

                <div className="react-shell-sidebar-body">
                    {isMenuLoading ? <div className="react-shell-menu-loading">Cargando menú...</div> : null}
                    {navGroups.map((group) => {
                        const isDirectSingleItem = !group.collapsible && group.items.length === 1 && Boolean(group.items[0]?.internalPath);
                        if (isDirectSingleItem) {
                            const singleItem = group.items[0];
                            return (
                                <section key={group.name} className="react-shell-nav-group react-shell-nav-group--direct">
                                    <NavLink
                                        className={({ isActive }) =>
                                            isActive ? 'react-shell-nav-top-item is-active' : 'react-shell-nav-top-item'
                                        }
                                        to={singleItem.internalPath}
                                        title={isSidebarCollapsed && !isMobileViewport ? singleItem.label : undefined}
                                        aria-label={singleItem.label}
                                        onClick={() => {
                                            setCollapsedFlyout(null);
                                            closeMobileMenu();
                                        }}
                                    >
                                        <group.icon className="react-shell-nav-group-icon" aria-hidden="true" />
                                        <span className="react-shell-nav-top-text">{singleItem.label}</span>
                                    </NavLink>
                                </section>
                            );
                        }

                        const isGroupActive = group.items.some(
                            (item) =>
                                item.internalPath &&
                                (currentPathname === item.internalPath || currentPathname.startsWith(`${item.internalPath}/`))
                        );
                        const isFlyoutOpen = collapsedFlyout?.groupName === group.name;

                        return (
                            <section key={group.name} className="react-shell-nav-group">
                                <button
                                    type="button"
                                    className={`react-shell-nav-title react-shell-nav-title-button${isGroupActive ? ' is-active' : ''}${isFlyoutOpen ? ' is-flyout-open' : ''}`}
                                    title={isSidebarCollapsed && !isMobileViewport ? group.name : undefined}
                                    aria-label={group.name}
                                    onClick={(event) => {
                                        if (isSidebarCollapsed && !isMobileViewport) {
                                            const rect = event.currentTarget.getBoundingClientRect();
                                            setCollapsedFlyout((prev) =>
                                                prev?.groupName === group.name
                                                    ? null
                                                    : {
                                                          groupName: group.name,
                                                          top: Math.max(12, Math.min(rect.top, window.innerHeight - 260)),
                                                      }
                                            );
                                            return;
                                        }
                                        if (group.collapsible) {
                                            handleToggleGroup(group.name);
                                        }
                                    }}
                                    aria-expanded={isSidebarCollapsed && !isMobileViewport ? isFlyoutOpen : !collapsedGroups[group.name]}
                                >
                                    <span className="react-shell-nav-group-label">
                                        <group.icon className="react-shell-nav-group-icon" aria-hidden="true" />
                                        <span>{group.name}</span>
                                    </span>
                                    {group.collapsible ? <ChevronDownIcon className={`react-shell-chevron${collapsedGroups[group.name] ? ' is-collapsed' : ''}`} aria-hidden="true" /> : null}
                                </button>

                                {!collapsedGroups[group.name] ? (
                                    <nav className="react-shell-nav">
                                        {group.items.map((item) => {
                                            if (item.internalPath) {
                                                return (
                                                    <NavLink
                                                        key={item.key}
                                                        className={({ isActive }) =>
                                                            isActive ? 'react-shell-nav-item is-active' : 'react-shell-nav-item'
                                                        }
                                                        to={item.internalPath}
                                                        onClick={closeMobileMenu}
                                                    >
                                                        <span className="react-shell-nav-text">{item.label}</span>
                                                    </NavLink>
                                                );
                                            }

                                            if (item.isEnabledExternal && item.externalPath) {
                                                return (
                                                    <a
                                                        key={item.key}
                                                        className="react-shell-nav-item"
                                                        href={item.externalPath}
                                                        onClick={closeMobileMenu}
                                                    >
                                                        <span className="react-shell-nav-text">{item.label}</span>
                                                    </a>
                                                );
                                            }

                                            if (item.isExternal && !item.isEnabledExternal) {
                                                return (
                                                    <span
                                                        key={item.key}
                                                        className="react-shell-nav-item is-disabled"
                                                        title="Módulo aún no disponible en React"
                                                    >
                                                        <span className="react-shell-nav-text">{item.label}</span>
                                                        <span className="react-shell-nav-badge-soon" aria-label="Próximamente">
                                                            Próximamente
                                                        </span>
                                                    </span>
                                                );
                                            }

                                            return (
                                                <span key={item.key} className="react-shell-nav-item is-disabled">
                                                    <span className="react-shell-nav-text">{item.label}</span>
                                                </span>
                                            );
                                        })}
                                    </nav>
                                ) : null}
                            </section>
                        );
                    })}

                    {hasPermission('testing.view') && (
                        <NavLink
                            className={({ isActive }) =>
                                isActive ? 'react-shell-nav-top-item is-active' : 'react-shell-nav-top-item'
                            }
                            to="/playground"
                            title={isSidebarCollapsed && !isMobileViewport ? 'Test Plugins (Nativo)' : undefined}
                            onClick={closeMobileMenu}
                            style={{ marginTop: 'auto', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}
                        >
                            <BeakerIcon className="react-shell-nav-group-icon" aria-hidden="true" />
                            <span className="react-shell-nav-top-text">Test Plugins (Nativo)</span>
                        </NavLink>
                    )}

                    <button
                        className="react-shell-logout"
                        type="button"
                        title={isSidebarCollapsed && !isMobileViewport ? 'Cerrar sesión' : undefined}
                        aria-label="Cerrar sesión"
                        onClick={logout}
                    >
                        <ArrowLeftOnRectangleIcon className="react-shell-nav-icon" aria-hidden="true" />
                        <span>Cerrar sesion</span>
                    </button>
                </div>

                {collapsedFlyout && isSidebarCollapsed && !isMobileViewport ? (
                    (() => {
                        const activeGroup = navGroups.find((g) => g.name === collapsedFlyout.groupName);
                        if (!activeGroup) {
                            return null;
                        }
                        return (
                            <div
                                ref={collapsedFlyoutRef}
                                className="react-shell-collapsed-flyout"
                                style={{ top: `${collapsedFlyout.top}px` }}
                                role="menu"
                                aria-label={activeGroup.name}
                            >
                                <div className="react-shell-collapsed-flyout-header">{activeGroup.name}</div>
                                <div className="react-shell-collapsed-flyout-list">
                                    {activeGroup.items.map((item) => {
                                        if (item.internalPath) {
                                            return (
                                                <NavLink
                                                    key={item.key}
                                                    className={({ isActive }) =>
                                                        isActive
                                                            ? 'react-shell-collapsed-flyout-item is-active'
                                                            : 'react-shell-collapsed-flyout-item'
                                                    }
                                                    to={item.internalPath}
                                                    onClick={() => setCollapsedFlyout(null)}
                                                >
                                                    <span>{item.label}</span>
                                                </NavLink>
                                            );
                                        }
                                        if (item.isEnabledExternal && item.externalPath) {
                                            return (
                                                <a
                                                    key={item.key}
                                                    className="react-shell-collapsed-flyout-item"
                                                    href={item.externalPath}
                                                    onClick={() => setCollapsedFlyout(null)}
                                                >
                                                    <span>{item.label}</span>
                                                </a>
                                            );
                                        }
                                        return (
                                            <span key={item.key} className="react-shell-collapsed-flyout-item is-disabled">
                                                <span>{item.label}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()
                ) : null}
            </aside>

            <div className="react-shell-main">
                <header className="react-shell-topbar">
                    <div className="react-shell-topbar-left">
                        <button
                            type="button"
                            className="react-shell-menu-toggle"
                            onClick={() => setIsMobileMenuOpen((value) => !value)}
                            aria-label="Abrir menu"
                        >
                            <Bars3Icon className="react-shell-nav-icon" aria-hidden="true" />
                        </button>
                        <ShiftTrackingControl compact />
                        {/*<div className="react-shell-topbar-title">Panel operativo</div>*/}
                    </div>

                    <div className="react-shell-topbar-actions">

                        {isOverflowDebugEnabled ? (
                            <span className="react-shell-debug-badge">DEBUG OVERFLOW</span>
                        ) : null}

                        {offlinePendingCount > 0 ? (
                            <span className="react-shell-debug-badge">
                                {offlineSyncing
                                    ? `SINCRONIZANDO ${offlinePendingCount}`
                                    : `PENDIENTES ${offlinePendingCount}`}
                            </span>
                        ) : null}

                        {offlineMediaPendingCount > 0 ? (
                            <span className="react-shell-debug-badge">
                                {offlineMediaSyncing
                                    ? `ARCHIVOS PENDIENTES ${offlineMediaPendingCount} (SYNC)`
                                    : `ARCHIVOS PENDIENTES ${offlineMediaPendingCount}`}
                            </span>
                        ) : null}

                        {offlineOperationPendingCount > 0 ? (
                            <span className="react-shell-debug-badge">
                                {offlineOperationSyncing
                                    ? `OPERACIONES ${offlineOperationPendingCount} (SYNC)`
                                    : `OPERACIONES ${offlineOperationPendingCount}`}
                            </span>
                        ) : null}

                        <AndroidUpdateControl token={token} onUpdateInfoChange={setApkUpdateInfo} />

                        {!isNativePlatform() ? <GlobalPwaControls /> : null}

                        {hasPermission('access.asistente_operativo_chat') ? (
                            <button
                                type="button"
                                onClick={() => setIsAiAssistantOpen(true)}
                                title={user?.empresa?.nombre_asistente_ia || 'Asistente Operativo'}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    padding: '5px 10px 5px 6px',
                                    borderRadius: '999px',
                                    border: '1px solid #CBD5E1',
                                    background: '#F8FAFC',
                                    color: '#0F172A',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                <img
                                    src={user?.empresa?.logo_asistente_ia_url || '/images/default-ai-bot.png'}
                                    alt={user?.empresa?.nombre_asistente_ia || 'Asistente'}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        background: '#FFFFFF',
                                    }}
                                />
                                <span>{user?.empresa?.nombre_asistente_ia || 'Asistente'}</span>
                            </button>
                        ) : null}

                        <ConnectionIndicator />

                        <div className="react-shell-notification-wrap">
                            <button
                                type="button"
                                className="react-shell-notification-btn"
                                aria-label="Abrir notificaciones"
                                onClick={async () => {
                                    const nextOpen = !notificationsOpen;
                                    setNotificationsOpen(nextOpen);
                                    if (nextOpen) {
                                        await loadNotifications();
                                        await loadOfflineDetails();
                                    }
                                }}
                            >
                                <BellAlertIcon className="react-shell-notification-icon" aria-hidden="true" />
                                {((unreadCount || 0) + (apkUpdateInfo?.hasNewVersion ? 1 : 0)) > 0 ? (
                                    <span className="react-shell-notification-badge">
                                        {((unreadCount || 0) + (apkUpdateInfo?.hasNewVersion ? 1 : 0)) > 99
                                            ? '99+'
                                            : ((unreadCount || 0) + (apkUpdateInfo?.hasNewVersion ? 1 : 0))}
                                    </span>
                                ) : null}
                            </button>

                            {notificationsOpen ? (
                                <div className="react-shell-notification-panel">
                                    <div className="react-shell-notification-panel-head">
                                        <div className="react-shell-notification-panel-title">
                                            <strong>Notificaciones</strong>
                                            {unreadCount > 0 ? (
                                                <span className="react-shell-notification-unread">
                                                    {unreadCount} nueva{unreadCount === 1 ? '' : 's'}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="react-shell-notification-panel-actions">
                                            {unreadCount > 0 ? (
                                                <button
                                                    type="button"
                                                    className="react-shell-notification-mark-all"
                                                    onClick={handleMarkAllRead}
                                                    title="Marcar todas como leídas"
                                                >
                                                    <CheckCircleIcon style={{ width: 15, height: 15, marginRight: 4 }} />
                                                    Marcar todas leídas
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="react-shell-notification-close"
                                                onClick={() => setNotificationsOpen(false)}
                                                aria-label="Cerrar notificaciones"
                                            >
                                                <XMarkIcon className="react-shell-notification-close-icon" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>

                                    {totalOfflinePending > 0 ? (
                                        <div className="react-shell-notification-apk-card">
                                            <div className="react-shell-notification-apk-badge">
                                                <SparklesIcon className="react-shell-notification-apk-icon" aria-hidden="true" />
                                                <span>Pendientes sin sincronizar</span>
                                            </div>
                                            <div className="react-shell-notification-item-title">
                                                {`Tenés ${totalOfflinePending} pendiente${totalOfflinePending === 1 ? '' : 's'} offline.`}
                                            </div>
                                            <div className="react-shell-notification-item-message">
                                                Puede incluir borradores, archivos u operaciones de otras visitas guardadas en este dispositivo.
                                            </div>
                                            <div className="react-shell-notification-apk-action">
                                                <button
                                                    type="button"
                                                    onClick={handleManualOfflineSync}
                                                    disabled={!isOnline || isAnyOfflineSyncing}
                                                    className="react-shell-notification-go"
                                                >
                                                    {isAnyOfflineSyncing ? 'Sincronizando...' : 'Sincronizar pendientes'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        setOfflineDetailsOpen((prev) => !prev);
                                                        if (!offlineDetailsOpen) {
                                                            await loadOfflineDetails();
                                                        }
                                                    }}
                                                    className="react-shell-notification-go"
                                                >
                                                    {offlineDetailsOpen ? 'Ocultar detalle' : 'Ver pendientes'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleDiscardAllOffline}
                                                    disabled={isAnyOfflineSyncing}
                                                    className="react-shell-notification-discard"
                                                >
                                                    Descartar
                                                </button>
                                            </div>

                                            {offlineDetailsOpen ? (
                                                <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
                                                    {offlineDetailsLoading ? (
                                                        <div className="react-shell-notification-item-message">Cargando pendientes...</div>
                                                    ) : null}

                                                    {!offlineDetailsLoading && offlineDetailsRows.length === 0 ? (
                                                        <div className="react-shell-notification-item-message">No hay pendientes offline para este usuario.</div>
                                                    ) : null}

                                                    {!offlineDetailsLoading && offlineDetailsRows.length > 0
                                                        ? offlineDetailsRows.map((row) => (
                                                            <div key={row.id} style={{ padding: '6px 0', borderTop: '1px solid #eef2ff' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <div className="react-shell-notification-item-title">{`${row.type} - Visita N° ${row.visitaId}`}</div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        {row.statusLabel ? (
                                                                            <span style={{
                                                                                fontSize: 10,
                                                                                fontWeight: 600,
                                                                                padding: '2px 6px',
                                                                                borderRadius: 4,
                                                                                backgroundColor: row.status === 'conflict' ? '#fef3c7' : '#f1f5f9',
                                                                                color: row.status === 'conflict' ? '#b45309' : '#475569',
                                                                            }}>
                                                                                {row.statusLabel}
                                                                            </span>
                                                                        ) : null}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDiscardSingleOffline(row)}
                                                                            disabled={isAnyOfflineSyncing}
                                                                            style={{
                                                                                fontSize: 10,
                                                                                fontWeight: 500,
                                                                                padding: '2px 6px',
                                                                                borderRadius: 4,
                                                                                border: '1px solid #fecaca',
                                                                                backgroundColor: '#fef2f2',
                                                                                color: '#b91c1c',
                                                                                cursor: isAnyOfflineSyncing ? 'not-allowed' : 'pointer',
                                                                            }}
                                                                            title="Descartar solo este pendiente"
                                                                        >
                                                                            Descartar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div className="react-shell-notification-item-message">
                                                                    {row.lastError ? `Motivo: ${row.lastError}` : (row.updatedAt ? `Último cambio: ${new Date(row.updatedAt).toLocaleString()}` : 'Sin fecha disponible')}
                                                                </div>
                                                            </div>
                                                        ))
                                                        : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    {/* Notificación destacada de nueva versión APK si está disponible */}
                                    {apkUpdateInfo?.hasNewVersion && apkUpdateInfo?.latest ? (
                                        <div
                                            className="react-shell-notification-apk-card"
                                            onClick={() => apkUpdateInfo.handleDownload()}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div className="react-shell-notification-apk-badge">
                                                    <SparklesIcon className="react-shell-notification-apk-icon" aria-hidden="true" />
                                                    <span>Actualización disponible</span>
                                                </div>
                                                {typeof apkUpdateInfo?.handleDismiss === 'function' ? (
                                                    <button
                                                        type="button"
                                                        className="react-shell-notification-item-check"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            apkUpdateInfo.handleDismiss();
                                                        }}
                                                        title="Marcar como visto / Descartar"
                                                    >
                                                        <XMarkIcon style={{ width: 14, height: 14 }} />
                                                    </button>
                                                ) : null}
                                            </div>
                                            <div className="react-shell-notification-item-title" style={{ marginTop: 4 }}>
                                                Nueva versión de la app {apkUpdateInfo.latest.version ? `v${apkUpdateInfo.latest.version}` : (apkUpdateInfo.latest.filename || '')}
                                            </div>
                                            <div className="react-shell-notification-item-message">
                                                Hay una nueva versión para tu dispositivo. Toca aquí para descargar e instalar el archivo APK.
                                            </div>
                                            <button
                                                type="button"
                                                className="react-shell-notification-apk-download-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    apkUpdateInfo.handleDownload();
                                                }}
                                            >
                                                <ArrowDownTrayIcon className="react-shell-notification-apk-download-icon" aria-hidden="true" />
                                                <span>Descargar actualización</span>
                                            </button>
                                        </div>
                                    ) : null}

                                    {notificationsLoading ? (
                                        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                            {[1, 2, 3].map((i) => (
                                                <Box key={i} sx={{ pb: 1, borderBottom: i < 3 ? '1px solid #f1f5f9' : 'none' }}>
                                                    <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: '4px' }} />
                                                    <Skeleton variant="text" width="95%" height={15} sx={{ mt: 0.5, borderRadius: '4px' }} />
                                                    <Skeleton variant="text" width="40%" height={12} sx={{ mt: 0.25, borderRadius: '4px' }} />
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : null}

                                    {!notificationsLoading && notifications.length === 0 && !apkUpdateInfo?.hasNewVersion ? (
                                        <EmptyState compact className="react-shell-notification-empty" message="No hay notificaciones." />
                                    ) : null}

                                    {!notificationsLoading && notifications.length > 0 ? (
                                        <div className="react-shell-notification-list">
                                            {notifications.map((notification) => {
                                                const isUnread = Number(notification.leida) !== 1;
                                                return (
                                                    <div
                                                        key={notification.id}
                                                        className={`react-shell-notification-item${isUnread ? ' is-unread' : ''}`}
                                                        onClick={() => handleOpenNotification(notification)}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        <div className="react-shell-notification-item-body">
                                                            <div className="react-shell-notification-item-title">{notification.titulo || 'Notificación'}</div>
                                                            <div className="react-shell-notification-item-message">{notification.mensaje || '-'}</div>
                                                        </div>
                                                        {isUnread ? (
                                                            <button
                                                                type="button"
                                                                className="react-shell-notification-item-check"
                                                                onClick={(e) => handleMarkSingleRead(e, notification)}
                                                                title="Marcar como visto / leída"
                                                            >
                                                                <CheckIcon style={{ width: 14, height: 14 }} />
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        <div className="react-shell-user-chip-wrap" ref={profileMenuRef}>
                            <button
                                type="button"
                                className="react-shell-user-chip"
                                title={user?.name || 'Usuario'}
                                onClick={() => setProfileMenuOpen((value) => !value)}
                                aria-haspopup="menu"
                                aria-expanded={profileMenuOpen}
                                aria-label="Abrir opciones de usuario"
                            >
                                <span className="react-shell-avatar">{userInitials}</span>
                                <span className="react-shell-user-name">{user?.name || 'Usuario'}</span>
                                <ChevronDownIcon className={`react-shell-user-chip-chevron${profileMenuOpen ? ' is-open' : ''}`} aria-hidden="true" />
                            </button>

                            {profileMenuOpen ? (
                                <div className="react-shell-user-menu" role="menu" aria-label="Opciones de usuario">
                                    <button type="button" className="react-shell-user-menu-item" role="menuitem" onClick={handleGoToMiCuenta}>
                                        Mi cuenta
                                    </button>
                                    <button type="button" className="react-shell-user-menu-item is-danger" role="menuitem" onClick={handleLogoutFromMenu}>
                                        Cerrar sesion
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </header>

                <main className="react-shell-content">
                    <AsistenciaScanRecoveryNotice />
                    <DataUsageLifecycle />
                    {isNetworkSessionExpired && !isReauthDismissed ? (
                        <div
                            role="alert"
                            style={{
                                backgroundColor: '#fff3cd',
                                color: '#664d03',
                                border: '1px solid #ffeeba',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '8px',
                                fontSize: '13px',
                                fontWeight: 500,
                                marginBottom: '16px',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <span>Necesitamos verificar tu acceso para enviar los cambios al servidor. Tu trabajo en este teléfono está seguro.</span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => setIsReauthModalOpen(true)}
                                    style={{
                                        background: '#0d6efd',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 12px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '12px',
                                    }}
                                >
                                    Verificar acceso
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsReauthDismissed(true)}
                                    style={{
                                        background: 'transparent',
                                        color: '#664d03',
                                        border: '1px solid #664d03',
                                        borderRadius: '4px',
                                        padding: '3px 8px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                    }}
                                >
                                    Descartar
                                </button>
                            </div>
                        </div>
                    ) : null}
                    {children}
                </main>
            </div>

            {activeNotification ? (
                <div className="react-shell-notification-modal-backdrop" onClick={() => setActiveNotification(null)}>
                    <div className="react-shell-notification-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="react-shell-notification-modal-head">
                            <h3>{activeNotification.titulo || 'Notificación'}</h3>
                            <button
                                type="button"
                                className="react-shell-notification-close"
                                onClick={() => setActiveNotification(null)}
                                aria-label="Cerrar modal"
                            >
                                <XMarkIcon className="react-shell-notification-close-icon" aria-hidden="true" />
                            </button>
                        </div>

                        <p className="react-shell-notification-modal-message">{activeNotification.mensaje || '-'}</p>

                        {activeNotification.url_destino ? (
                            <button type="button" className="react-shell-notification-go" onClick={handleGoToNotification}>
                                Ir al detalle
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {isMobileMenuOpen ? <button className="react-shell-backdrop" type="button" onClick={closeMobileMenu} aria-label="Cerrar menu" /> : null}

            <ReauthDialog
                open={Boolean(isReauthModalOpen)}
                onClose={() => setIsReauthModalOpen(false)}
                onReauthSuccess={() => {
                    setIsReauthModalOpen(false);
                    setIsReauthDismissed(false);
                    setIsNetworkSessionExpired(false);
                    isSessionExpiredRef.current = false;
                    flushAllOfflineQueues();
                }}
            />

            {hasPermission('access.asistente_operativo_chat') ? (
                <AsistenteOperativoSheet
                    open={Boolean(isAiAssistantOpen)}
                    onClose={() => setIsAiAssistantOpen(false)}
                />
            ) : null}
        </div>
    );
}

export default function AppShell({ children }) {
    const nested = React.useContext(AppShellContext);
    if (nested) return children;
    return <AppShellContext.Provider value={true}><AppShellContent>{children}</AppShellContent></AppShellContext.Provider>;
}
